import React from "react";
import Md from "../onboarding/Markdown.jsx";
import { fmtTime, fmtDate, BRAND, GOLD } from "../onboarding/helpers.js";

// Mirrors server/lib/client-esign/engine.js `shown` — the server stays authoritative.
export function shown(when, data) {
  if (!when) return true;
  if (Array.isArray(when)) return when.every((w) => shown(w, data));
  if (when.in) return when.in.includes(String(data[when.key] ?? ""));
  if (when.has) return data[`${when.key}.${when.has}`] === "1";
  return true;
}

/**
 * Renders a client e-sign document.
 * role: "signer" | "agency"  — the viewer; items owned by the other party are read-only.
 * locked: viewer cannot change anything. lockedKeys: individual keys the viewer may no longer change.
 */
export default function ClientDocRenderer({ template, data, role, locked, lockedKeys = [], images, stamps, signerRole, onChange, onStamp, busyKey }) {
  const can = (owner, key) => !locked && owner === role && !lockedKeys.includes(key);
  const src = (st, kind) => (st && typeof st === "object" ? (images?.[st.by || "signer"] || {})[kind] : null);
  const agencyTint = (owner) => (owner === "agency" ? "bg-amber-50/70 border-amber-200" : "bg-white");
  const ownerNote = (owner) => (owner === "agency" && role === "signer" ? <span className="text-[11px] text-amber-700 ml-1">· completed by Jybek</span> : owner === "signer" && role === "agency" ? <span className="text-[11px] text-blue-700 ml-1">· completed by the client / representative</span> : null);
  const stampBtn = (key, kind) => (
    <button type="button" disabled={busyKey === key} onClick={() => onStamp(key)}
      className={`rounded-lg font-semibold text-white disabled:opacity-60 ${kind === "sig" ? "px-4 py-2 text-sm" : "px-3 py-1.5 text-xs"}`} style={{ backgroundColor: kind === "sig" ? BRAND : GOLD }}>
      {busyKey === key ? "…" : kind === "sig" ? "Click to sign" : "Initial"}
    </button>
  );
  const stampImg = (st, kind) => { const s = src(st, kind); return s ? <img src={s} alt={kind} className={kind === "sig" ? "h-10 max-w-[220px] object-contain" : "h-7 object-contain mx-auto"} /> : null; };

  const input = (it, owner) => {
    const key = it.key;
    const common = {
      key, defaultValue: data[key] || "", placeholder: it.placeholder || "",
      onBlur: (e) => { if ((e.target.value || "") !== (data[key] || "")) onChange({ [key]: e.target.value }); },
      className: `w-full border rounded-md px-2 py-1.5 text-sm ${owner === "agency" ? "bg-amber-50 border-amber-200" : "bg-white border-gray-300"}`,
    };
    if (it.type === "textarea") return <textarea rows={3} {...common} />;
    return <input type={it.type === "date" ? "date" : it.type === "email" ? "email" : it.type === "tel" ? "tel" : "text"} inputMode={it.pattern ? "numeric" : undefined} {...common} />;
  };

  const out = [];
  template.model.forEach((el, i) => {
    if (!shown(el.when, data)) return;
    if (el.t === "note" && el.audience === "agency" && role !== "agency") return;
    switch (el.t) {
      case "h": out.push(<h3 key={i} className="text-lg font-bold mt-5" style={{ color: "#1B3A6B" }}><Md text={el.text} /></h3>); break;
      case "p": out.push(<p key={i} className="text-[15px] leading-relaxed"><Md text={el.text} /></p>); break;
      case "note": out.push(<div key={i} className={`border-l-4 px-3 py-2 text-sm rounded-r ${el.audience === "agency" ? "bg-amber-50 border-amber-400" : "bg-slate-50"}`} style={el.audience ? {} : { borderColor: BRAND }}><Md text={el.text} /></div>); break;
      case "bullets": out.push(<ul key={i} className="list-disc ml-6 space-y-1 text-[15px]">{el.items.map((x, n) => <li key={n}><Md text={x} /></li>)}</ul>); break;
      case "fields": {
        const items = el.items.filter((it) => shown(it.when, data));
        out.push(
          <div key={i} className={`grid sm:grid-cols-2 gap-3 my-2 rounded-lg ${el.role === "agency" ? "border p-3 " + agencyTint("agency") : ""}`}>
            {items.map((it) => (
              <div key={it.key} className={it.wide || it.type === "textarea" ? "sm:col-span-2" : ""}>
                <label className="block text-[11px] uppercase tracking-wide text-gray-500">{it.label}{it.required && can(el.role, it.key) ? " *" : ""}{ownerNote(el.role)}</label>
                {can(el.role, it.key) ? input(it, el.role) : <div className="border-b border-gray-300 min-h-[28px] py-1 font-semibold whitespace-pre-wrap">{it.type === "date" && data[it.key] ? fmtDate(data[it.key] + "T12:00:00Z") : data[it.key] || ""}</div>}
              </div>
            ))}
          </div>,
        );
        break;
      }
      case "choice": {
        const editable = can(el.role, el.key);
        out.push(
          <div key={i} className={`my-2 rounded-lg ${el.role === "agency" ? "border p-3 " + agencyTint("agency") : ""}`}>
            {el.label && <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{el.label}{el.required && editable ? " *" : ""}{ownerNote(el.role)}</div>}
            {el.options.map((o) => (
              <label key={o.value} className={`flex items-start gap-2 py-1.5 ${editable ? "cursor-pointer" : ""}`}>
                <input type="radio" className="mt-1.5 accent-[#154E93]" name={el.key} disabled={!editable} checked={String(data[el.key] ?? "") === o.value} onChange={() => onChange({ [el.key]: o.value })} />
                <span className="text-[15px]"><Md text={o.text} /></span>
              </label>
            ))}
          </div>,
        );
        break;
      }
      case "checks": {
        out.push(
          <div key={i} className="my-3">
            <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{el.label}{el.minOne && can(el.role, el.key) ? " * (at least one)" : ""}{ownerNote(el.role)}</div>
            <div className="border rounded-lg divide-y">
              {el.items.map((it) => {
                const k = `${el.key}.${it.id}`;
                const on = data[k] === "1";
                const editable = can(el.role, k);
                const iniKey = `${k}.ini`;
                return (
                  <div key={it.id} className="flex items-center gap-3 p-2.5">
                    <input type="checkbox" className="w-5 h-5 shrink-0 accent-[#154E93]" disabled={!editable} checked={on} onChange={(e) => onChange({ [k]: e.target.checked ? "1" : "" })} />
                    <div className="flex-1 text-[15px]"><Md text={it.text} /></div>
                    {el.initials && (
                      <div className="w-24 shrink-0 text-center">
                        {on && data[iniKey] ? stampImg(data[iniKey], "init") : on && stamps?.[iniKey] && !locked ? stampBtn(iniKey, "init") : <span className="block h-7" />}
                        <small className="block text-[9px] tracking-wider text-gray-500 border-t border-gray-700 mt-1">INITIALS</small>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>,
        );
        break;
      }
      case "initials":
        out.push(
          <div key={i} className="my-3">
            <div className="text-sm font-medium mb-1">{el.label}</div>
            <div className="border rounded-lg divide-y">
              {el.items.map((it) => {
                const k = `${el.key}.${it.id}`;
                return (
                  <div key={it.id} className="flex items-center gap-3 p-2.5">
                    <div className="flex-1 text-[15px]"><Md text={it.text} /></div>
                    <div className="w-24 shrink-0 text-center">
                      {data[k] ? stampImg(data[k], "init") : stamps?.[k] && !locked ? stampBtn(k, "init") : <span className="block h-7" />}
                      <small className="block text-[9px] tracking-wider text-gray-500 border-t border-gray-700 mt-1">INITIALS</small>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>,
        );
        break;
      case "sign": {
        const st = data[el.key];
        const who = el.who.replace("{signer_role}", signerRole || "Client");
        const titleKey = `${el.key}.title`;
        out.push(
          <div key={i} className={`mt-4 rounded-lg p-3 border ${agencyTint(el.role)}`}>
            <div className="text-[11px] font-bold tracking-widest uppercase" style={{ color: GOLD }}>{who}{el.role === "agency" && role === "signer" ? <span className="normal-case tracking-normal font-normal text-amber-700"> · signed by Jybek</span> : null}</div>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <div className="sm:flex-[2] border-b border-gray-700 pb-1 min-h-[48px]">
                <label className="block text-[10px] uppercase tracking-wide text-gray-500">Signature</label>
                {st ? <>{stampImg(st, "sig")}<span className="block text-[10px] text-gray-500">Signed electronically {fmtTime(st.at)}{st.ip ? ` · IP ${st.ip}` : ""}</span></> : stamps?.[el.key] && !locked && el.role === role ? stampBtn(el.key, "sig") : <span className="block h-8" />}
              </div>
              <div className="sm:flex-1 border-b border-gray-700 pb-1"><label className="block text-[10px] uppercase tracking-wide text-gray-500">Printed name</label><span className="font-semibold">{st ? st.name : ""}</span></div>
              {el.title ? (
                <div className="sm:flex-1 border-b border-gray-700 pb-1"><label className="block text-[10px] uppercase tracking-wide text-gray-500">Title</label>
                  {can(el.role, titleKey) && !st ? <input key={titleKey} defaultValue={data[titleKey] || ""} onBlur={(e) => e.target.value !== (data[titleKey] || "") && onChange({ [titleKey]: e.target.value })} className="w-full border rounded px-2 py-1 text-sm bg-amber-50 border-amber-200" /> : <span className="font-semibold">{data[titleKey] || st?.title || ""}</span>}
                </div>
              ) : null}
              <div className="sm:flex-1 border-b border-gray-700 pb-1"><label className="block text-[10px] uppercase tracking-wide text-gray-500">Date</label><span className="font-semibold">{st ? fmtDate(st.at) : ""}</span></div>
            </div>
          </div>,
        );
        break;
      }
      default: break;
    }
  });
  return <div className="space-y-2 text-gray-800">{out}</div>;
}
